// One-shot backfill UI for the address-in-notes cleanup. Auto-hides
// when there's nothing to clean. When candidates exist, shows the
// count + a Migrate button that calls the admin backfill endpoint.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Wand2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export default function AddressBackfillPanel({ currentUser, showToast = () => {} }) {
  const [count, setCount]     = React.useState(null);
  const [sample, setSample]   = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]       = React.useState(false);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const r = await apiFetch('/api/admin/backfill-address-notes');
      setCount(r.candidates_count || 0);
      setSample(r.sample || []);
    } catch (err) {
      showToast(`Backfill check failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, showToast]);

  React.useEffect(() => { load(); }, [load]);

  const migrate = async () => {
    if (busy) return;
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: `Migrate ${count} policy notes to structured addresses?`,
      body: 'Re-importable — address text moves to client_address / city / state / zip and the notes field clears.',
      confirmLabel: 'Migrate addresses',
    }) : Promise.resolve(window.confirm(`Migrate ${count} policy notes into structured address columns?`)));
    if (!ok) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/admin/backfill-address-notes', {
        method: 'POST',
        body: JSON.stringify({ dry_run: false }),
      });
      showToast(`Migrated ${r.migrated} policies${r.failures?.length ? ` · ${r.failures.length} failed` : ''}.`, 'success');
      await load();
      try { window.dispatchEvent(new CustomEvent('ct:health-client-changed')); } catch {}
    } catch (err) {
      showToast(`Migrate failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading || count == null || count === 0) return null;

  return (
    <div className="bg-white border border-amber-300 ring-1 ring-amber-100 rounded-xl p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <h3 className="font-bold text-slate-900 inline-flex items-center gap-2">
          <Wand2 size={14} className="text-amber-600"/>
          Clean up addresses in notes
        </h3>
        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
          {count} polic{count === 1 ? 'y' : 'ies'}
        </span>
      </div>
      <p className="text-sm text-slate-600 mb-3">
        Older HealthSherpa imports stored the client's address in the policy <strong>notes</strong> field instead of structured columns. New imports route correctly; this is a one-shot cleanup of the backlog.
      </p>
      {sample.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 max-h-40 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Sample notes content</div>
          <ul className="space-y-1 text-xs text-slate-700">
            {sample.map((s, i) => (
              <li key={s.policy_id} className="font-mono truncate">{i + 1}. {s.notes_preview}…</li>
            ))}
          </ul>
        </div>
      )}
      <button
        onClick={migrate}
        disabled={busy}
        className="text-sm font-bold uppercase tracking-wider bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-4 py-2 disabled:opacity-50 inline-flex items-center gap-2"
      >
        {busy ? 'Migrating…' : `Migrate all ${count}`}
      </button>
    </div>
  );
}
